import { TerraDraw } from "terra-draw";

type PolygonPathPoint = {
  lat: () => number;
  lng: () => number;
};

type GeometryCoordinate = number[];
type GeometryCoordinates =
  | Array<GeometryCoordinate>
  | Array<Array<GeometryCoordinate>>;

interface GeometryLike {
  type?: string;
  coordinates?: unknown;
}

interface PolygonBaseLike {
  Id?: string;
  id?: string;
  geometry?: GeometryLike;
  getPath?: () => {
    getArray: () => PolygonPathPoint[];
  };
  setMap: () => void;
  setEditable?: (editable: boolean) => void;
  setOptions?: (options: google.maps.PolygonOptions) => void;
}

export type PolygonPath = {
  lat: number;
  lng: number;
};

export type PolygonLike = PolygonBaseLike;

export interface GooglePolygonLike extends PolygonBaseLike {
  clickable?: boolean;
  editable?: boolean;
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
}

export interface DrawingManagerOptions {
  polygonOptions?: google.maps.PolygonOptions;
  [key: string]: unknown;
}

export type DrawSnapshotFeatureId = Parameters<TerraDraw["getSnapshotFeature"]>[0];

type DrawSnapshotFeature = NonNullable<
  ReturnType<TerraDraw["getSnapshotFeature"]>
>;

type TerraDrawStyleValue = number | `#${string}`;

function isGeometryCoordinate(value: unknown): value is GeometryCoordinate {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number"
  );
}

function isCoordinateRing(value: unknown): value is Array<GeometryCoordinate> {
  return Array.isArray(value) && value.every((item) => isGeometryCoordinate(item));
}

function getGeometryLine(geometry?: GeometryLike): Array<GeometryCoordinate> {
  if (!geometry || !Array.isArray(geometry.coordinates)) {
    return [];
  }

  if (geometry.type === "Polygon") {
    const firstRing = geometry.coordinates[0];
    return isCoordinateRing(firstRing) ? firstRing : [];
  }

  if (geometry.type === "LineString") {
    return isCoordinateRing(geometry.coordinates) ? geometry.coordinates : [];
  }

  if (isCoordinateRing(geometry.coordinates[0])) {
    return geometry.coordinates[0];
  }

  return isCoordinateRing(geometry.coordinates)
    ? geometry.coordinates
    : [];
}

function areSameCoordinate(
  first: GeometryCoordinate,
  second: GeometryCoordinate,
): boolean {
  return first[0] === second[0] && first[1] === second[1];
}

function ensureClosedRing(
  line: Array<GeometryCoordinate>,
): Array<GeometryCoordinate> {
  if (line.length < 3) {
    return line;
  }

  const first = line[0];
  const last = line[line.length - 1];

  if (areSameCoordinate(first, last)) {
    return line;
  }

  return [...line, [...first]];
}

export function isValidFinishedGeometry(geometry?: GeometryLike): boolean {
  const line = ensureClosedRing(getGeometryLine(geometry));

  if (line.length < 4) {
    return false;
  }

  const first = line[0];
  const last = line[line.length - 1];
  return areSameCoordinate(first, last);
}

export function mapPolygonStyles(
  polygonOptions: google.maps.PolygonOptions = {},
): Record<string, TerraDrawStyleValue> {
  const baseColor = (polygonOptions.strokeColor ||
    polygonOptions.fillColor ||
    "#00FF00") as `#${string}`;
  const strokeWeight =
    typeof polygonOptions.strokeWeight === "number"
      ? polygonOptions.strokeWeight
      : 1;
  const strokeOpacity =
    typeof polygonOptions.strokeOpacity === "number"
      ? polygonOptions.strokeOpacity
      : 1;
  const fillOpacity =
    typeof polygonOptions.fillOpacity === "number"
      ? polygonOptions.fillOpacity
      : 0.15;

  return {
    lineStringWidth: strokeWeight,
    lineStringColor: (polygonOptions.strokeColor || baseColor) as `#${string}`,
    lineStringOpacity: strokeOpacity,
    polygonFillColor: (polygonOptions.fillColor || baseColor) as `#${string}`,
    polygonFillOpacity: fillOpacity,
    polygonOutlineColor: (polygonOptions.strokeColor || baseColor) as `#${string}`,
    polygonOutlineWidth: strokeWeight,
    polygonOutlineOpacity: strokeOpacity,
    closingPointColor: baseColor,
    closingPointWidth: 6,
    closingPointOpacity: 1,
    closingPointOutlineColor: baseColor,
    closingPointOutlineWidth: 1,
    closingPointOutlineOpacity: 1,
    snappingPointColor: baseColor,
    snappingPointWidth: 6,
    snappingPointOpacity: 1,
    snappingPointOutlineColor: baseColor,
    snappingPointOutlineWidth: 1,
    snappingPointOutlineOpacity: 1,
  };
}

export function createSyntheticPolygon(
  featureId: DrawSnapshotFeatureId,
  geometry: DrawSnapshotFeature["geometry"],
): GooglePolygonLike {
  const polygonCoordinates = ensureClosedRing(getGeometryLine(geometry));

  return {
    Id: String(featureId),
    geometry: geometry as GooglePolygonLike["geometry"],
    clickable: true,
    editable: true,
    fillColor: "#00FF00",
    fillOpacity: 0.15,
    strokeColor: "#00FF00",
    strokeOpacity: 1,
    strokeWeight: 1,
    getPath: () => ({
      getArray: () =>
        polygonCoordinates.map(([lng, lat]) => ({
          lat: () => lat,
          lng: () => lng,
        })),
    }),
    setMap: () => undefined,
    setEditable: () => undefined,
    setOptions: () => undefined,
  };
}

export function getPolygonPaths(poly: PolygonLike): PolygonPath[] {
  if (poly && typeof poly.getPath === "function") {
    return poly
      .getPath()
      .getArray()
      .map((path) => ({
        lat: path.lat(),
        lng: path.lng(),
      }));
  }

  const coordinates = poly && poly.geometry && poly.geometry.coordinates;

  if (!coordinates) {
    return [];
  }

  return ensureClosedRing(getGeometryLine(poly.geometry)).map(([lng, lat]) => ({
    lat,
    lng,
  }));
}
